import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { PaperAirplaneIcon, SparklesIcon } from '@heroicons/react/24/outline';

import { streamModuleBuilder } from '@/api/moduleBuilder';
import MarkdownViewer from '@/components/core/MarkdownViewer';

/**
 * Chat drawer for the equation module-builder agent. Manages its own
 * conversation state (ephemeral — lost on modal close). Delegates form
 * mutations and test results to the parent via callbacks so the modal
 * keeps a single source of truth.
 */
const AIChatPanel = ({
  getFormSnapshot,
  getAnnotations,
  getLastTestResult,
  onModuleUpdate,
  onTestResult,
}) => {
  // Each entry is one of:
  //   { role: 'user', text }
  //   { role: 'assistant', text, toolCalls: [{id, name, input, result?}] }
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const scrollerRef = useRef(null);
  const mountedRef = useRef(true);

  // The panel unmounts both when the modal closes and when the user toggles
  // the AI drawer shut. Either way an in-flight stream would keep the server
  // agent running and keep feeding `onModuleUpdate` into a form the user has
  // navigated away from — so abort on the way out.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (scrollerRef.current) {
        scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
      }
    });
  }, []);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput('');
    setError(null);

    // Append the user turn locally and build the wire history. The wire
    // history uses Anthropic's message shape; our internal `messages`
    // array is a flatter UI representation.
    const userTurn = { role: 'user', text };
    const newMessages = [
      ...messages,
      userTurn,
      { role: 'assistant', text: '', thinking: '', toolCalls: [] },
    ];
    setMessages(newMessages);

    // Build the wire conversation: alternating user/assistant turns with
    // string content. Tool calls/results aren't echoed back from the
    // server-held history (they're internal to one turn).
    const wireMessages = [
      ...messages.map(m => ({ role: m.role, content: m.text || '' })),
      { role: 'user', content: text },
    ].filter(m => m.content);

    const controller = new AbortController();
    abortRef.current = controller;
    setStreaming(true);

    try {
      await streamModuleBuilder({
        messages: wireMessages,
        formState: getFormSnapshot(),
        annotations: getAnnotations(),
        lastTestResult: getLastTestResult(),
        signal: controller.signal,
        onText: chunk => {
          setMessages(prev => {
            const out = [...prev];
            const last = out[out.length - 1];
            out[out.length - 1] = { ...last, text: (last.text || '') + chunk };
            return out;
          });
          scrollToBottom();
        },
        onThinking: chunk => {
          setMessages(prev => {
            const out = [...prev];
            const last = out[out.length - 1];
            out[out.length - 1] = {
              ...last,
              thinking: (last.thinking || '') + chunk,
            };
            return out;
          });
        },
        onToolUseStart: ({ id, name, input: toolInput }) => {
          setMessages(prev => {
            const out = [...prev];
            const last = out[out.length - 1];
            out[out.length - 1] = {
              ...last,
              toolCalls: [...(last.toolCalls || []), { id, name, input: toolInput }],
            };
            return out;
          });
          scrollToBottom();
        },
        onToolResult: ({ id, result }) => {
          setMessages(prev => {
            const out = [...prev];
            const last = out[out.length - 1];
            out[out.length - 1] = {
              ...last,
              toolCalls: (last.toolCalls || []).map(tc =>
                tc.id === id ? { ...tc, result } : tc,
              ),
            };
            return out;
          });
        },
        // These two write into the parent's form. An abort can't stop an
        // event already in the read buffer, so drop anything that arrives
        // after unmount rather than mutating a form we no longer own.
        onModuleUpdate: partial => {
          if (mountedRef.current) onModuleUpdate(partial);
        },
        onTestResult: result => {
          if (mountedRef.current) onTestResult(result);
        },
        onError: msg => setError(msg),
        onDone: () => setStreaming(false),
      });
    } catch (err) {
      setError(err.message || 'Stream failed');
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  }, [input, streaming, messages, getFormSnapshot, getAnnotations, getLastTestResult,
      onModuleUpdate, onTestResult, scrollToBottom]);

  const stop = () => {
    abortRef.current?.abort();
    setStreaming(false);
  };

  return (
    <div className="flex flex-col h-full bg-gray-50 border-l border-gray-200">
      <div className="px-3 py-2 border-b border-gray-200 flex items-center gap-2">
        <SparklesIcon className="h-4 w-4 text-purple-500" />
        <span className="font-semibold text-sm">AI assistant</span>
        <span className="text-[10px] text-gray-400 ml-auto">beta · ephemeral</span>
      </div>

      <div ref={scrollerRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3 text-xs">
        {messages.length === 0 && (
          <div className="text-gray-500 italic">
            Describe the process you want to model — e.g. <em>&ldquo;a natural-gas combined-cycle
            power plant with 55% efficiency.&rdquo;</em>
          </div>
        )}
        {messages.map((m, idx) => (
          <Message key={idx} message={m} />
        ))}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded p-2 text-red-700">
            {error}
          </div>
        )}
      </div>

      <div className="border-t border-gray-200 p-2">
        <div className="flex gap-2">
          <textarea
            className="textarea textarea-xs textarea-bordered flex-1 resize-none"
            rows={2}
            placeholder="Ask the AI to build or change the module…"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            disabled={streaming}
          />
          {streaming ? (
            <button
              type="button"
              className="btn btn-xs btn-ghost self-end"
              onClick={stop}
            >
              Stop
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-xs btn-primary self-end"
              onClick={send}
              disabled={!input.trim()}
            >
              <PaperAirplaneIcon className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

AIChatPanel.propTypes = {
  getFormSnapshot: PropTypes.func.isRequired,
  getAnnotations: PropTypes.func.isRequired,
  getLastTestResult: PropTypes.func.isRequired,
  onModuleUpdate: PropTypes.func.isRequired,
  onTestResult: PropTypes.func.isRequired,
};

const Message = ({ message }) => {
  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] bg-blue-50 border border-blue-200 rounded px-2 py-1 whitespace-pre-wrap">
          {message.text}
        </div>
      </div>
    );
  }
  // assistant — render markdown so bold, code blocks, lists format correctly.
  // prose-sm bumps font to text-sm; max-w-none prevents the prose default
  // 65ch width from squeezing inside the narrow drawer.
  return (
    <div className="space-y-1">
      {message.thinking ? (
        <details className="text-[10px] text-gray-500">
          <summary className="cursor-pointer select-none italic hover:text-gray-700">
            thinking
          </summary>
          <div className="mt-1 ml-2 pl-2 border-l-2 border-gray-200 whitespace-pre-wrap font-mono text-[10px] text-gray-500">
            {message.thinking}
          </div>
        </details>
      ) : null}
      {message.text ? (
        <MarkdownViewer
          content={message.text}
          className="prose prose-sm max-w-none text-gray-800"
        />
      ) : null}
      {(message.toolCalls || []).map(tc => (
        <ToolCallRow key={tc.id} call={tc} />
      ))}
    </div>
  );
};

Message.propTypes = {
  message: PropTypes.object.isRequired,
};

const ToolCallRow = ({ call }) => {
  const isUpdate = call.name === 'update_module';
  const isTest = call.name === 'run_test';
  const isState = call.name === 'get_module_state';
  const label = (() => {
    if (isUpdate) {
      const keys = Object.keys(call.input || {});
      return `updated ${keys.join(', ') || '(nothing)'}`;
    }
    if (isTest) return 'ran test';
    if (isState) return 'read module state';
    return call.name;
  })();
  const hasError = call.result && call.result.error;
  return (
    <div
      className={`text-[10px] font-mono rounded px-2 py-0.5 inline-block ${
        hasError
          ? 'bg-red-50 text-red-700 border border-red-200'
          : 'bg-gray-100 text-gray-600 border border-gray-200'
      }`}
    >
      {hasError ? `✕ ${label} — ${call.result.error}` : `› ${label}`}
    </div>
  );
};

ToolCallRow.propTypes = {
  call: PropTypes.object.isRequired,
};

export default AIChatPanel;
